import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  Send,
  Add,
  SmartToy,
  ReceiptLong,
  TrendingUp,
  ShowChart,
  CheckCircle,
  Assessment,
  Close,
  Folder,
  Event,
  KeyboardArrowUp,
  KeyboardArrowDown
} from '@mui/icons-material';
import { Project, Meeting } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useJiraAgent } from '../../useJiraAgent';

interface ChatPanelProps {
  selectedProject: Project;
  selectedMeeting: Meeting | null;
  onOpenProjectsDrawer?: () => void;
  onOpenMeetingsDrawer?: () => void;
  leftSidebarOpen?: boolean;
  rightSidebarOpen?: boolean;
  onToggleLeftSidebar?: () => void;
  onToggleRightSidebar?: () => void;
  isMobile?: boolean;
}

interface MessageItem {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  cardType?: 'summary' | 'health' | 'jira' | 'brd' | 'blockers';
  timestamp: string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  selectedProject,
  selectedMeeting,
  onOpenProjectsDrawer,
  onOpenMeetingsDrawer,
  leftSidebarOpen,
  rightSidebarOpen,
  onToggleLeftSidebar,
  onToggleRightSidebar,
  isMobile
}) => {
  const { user } = useAuth();
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [welcomeExpanded, setWelcomeExpanded] = useState(true);
  const [dialogInfo, setDialogInfo] = useState<{ title: string; content: React.ReactNode } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const agent = useJiraAgent();

  const userName = user?.displayName ? user.displayName.split(' ')[0] : 'there';
  const userAvatar = user?.photoURL || 'https://i.pravatar.cc/150?u=scrumai';

  const [messages, setMessages] = useState<MessageItem[]>([
    // {
    //   id: 'm1',
    //   sender: 'user',
    //   text: "Give me a summary of today's product meeting",
    //   timestamp: '10:46 AM'
    // },
    // {
    //   id: 'm2',
    //   sender: 'assistant',
    //   text: "Here's a comprehensive summary of today's product sync grounded in the transcript:",
    //   cardType: 'summary',
    //   timestamp: '10:46 AM'
    // }
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSendMessage = async (textToSend?: string) => {
    const query = (textToSend || input).trim();
    if (!query) return;

    if (agent.isConnected) {
      // User is authenticated with the Jira Agent
      const userMsg: MessageItem = {
        id: `u-${Date.now()}`,
        sender: 'user',
        text: query,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      const botMsgId = `bot-${Date.now()}`;
      const emptyBotMsg: MessageItem = {
        id: botMsgId,
        sender: 'assistant',
        text: '',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages((prev) => [...prev, userMsg, emptyBotMsg]);
      setInput('');
      
      await agent.send(query, (chunk: string) => {
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, text: last.text + chunk };
          return copy;
        });
      });
      return;
    }

    // Fallback to legacy simulated response if Jira Agent is disconnected or missing cookies
    const userMsg: MessageItem = {
      id: `u-${Date.now()}`,
      sender: 'user',
      text: query,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    // Simulate grounded AI response
    setTimeout(() => {
      let cardType: MessageItem['cardType'] = undefined;
      let replyText = `I have analyzed your request regarding "${query}" for ${selectedProject.name}.`;

      const lower = query.toLowerCase();
      if (lower.includes('health') || lower.includes('progress') || lower.includes('sprint')) {
        cardType = 'health';
        replyText = `Here is the current Scrum Health analysis for ${selectedProject.name}:`;
      } else if (lower.includes('jira') || lower.includes('story') || lower.includes('epic') || lower.includes('action')) {
        cardType = 'jira';
        replyText = `Here are the latest Jira tickets created by ScrumAI from recent discussions:`;
      } else if (lower.includes('brd') || lower.includes('requirement') || lower.includes('template')) {
        cardType = 'brd';
        replyText = `Generated draft BRD (Business Requirements Document) for ${selectedProject.name}:`;
      } else if (lower.includes('blocker') || lower.includes('risk')) {
        cardType = 'blockers';
        replyText = `Identified delivery blockers and risks for ${selectedProject.name}:`;
      } else if (lower.includes('summary') || lower.includes('meeting')) {
        cardType = 'summary';
        replyText = selectedMeeting
          ? `Here is the meeting intelligence breakdown for ${selectedMeeting.title}:`
          : `Here is the meeting intelligence breakdown for recent sessions:`;
      }

      const botMsg: MessageItem = {
        id: `bot-${Date.now()}`,
        sender: 'assistant',
        text: replyText,
        cardType,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 800);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5, width: '100%', minWidth: 0 }}>
      
      {/* Top Context Bar & View Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: { xs: 1, sm: 1.5 },
          py: 0.8,
          borderRadius: '14px',
          bgcolor: 'var(--hp-surface-raised)',
          border: '1px solid var(--hp-border)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
          flexShrink: 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {/* Desktop Left Sidebar Toggle */}
          {onToggleLeftSidebar && !isMobile && (
            <Tooltip title={leftSidebarOpen ? "Collapse Projects Sidebar" : "Expand Projects Sidebar"}>
              <IconButton
                size="small"
                onClick={onToggleLeftSidebar}
                sx={{
                  color: leftSidebarOpen ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
                  bgcolor: leftSidebarOpen ? 'var(--hp-primary-light)' : 'transparent',
                  p: 0.6,
                  borderRadius: '8px'
                }}
              >
                <Folder fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Active Project Chip */}
          <Tooltip title={isMobile ? "Tap to switch project" : "Active project context"}>
            <Chip
              icon={<Folder sx={{ fontSize: 16 }} />}
              label={selectedProject.name}
              onClick={onOpenProjectsDrawer || onToggleLeftSidebar}
              size="small"
              sx={{
                maxWidth: { xs: 150, sm: 220, md: 280 },
                fontWeight: 600,
                fontSize: '0.8rem',
                bgcolor: 'var(--hp-surface)',
                border: '1px solid var(--hp-border)',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'var(--hp-primary-light)', borderColor: 'var(--hp-primary)' }
              }}
            />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {/* Active Meeting Chip */}
          <Tooltip title={isMobile ? "Tap to switch meeting" : "Active meeting context"}>
            <Chip
              icon={<Event sx={{ fontSize: 16 }} />}
              label={selectedMeeting ? selectedMeeting.title : 'No Meeting Selected'}
              onClick={onOpenMeetingsDrawer || onToggleRightSidebar}
              size="small"
              sx={{
                maxWidth: { xs: 150, sm: 220, md: 280 },
                fontWeight: 600,
                fontSize: '0.8rem',
                bgcolor: 'var(--hp-surface)',
                border: '1px solid var(--hp-border)',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'var(--hp-primary-light)', borderColor: 'var(--hp-primary)' }
              }}
            />
          </Tooltip>

          {/* Desktop Right Sidebar Toggle */}
          {onToggleRightSidebar && !isMobile && (
            <Tooltip title={rightSidebarOpen ? "Collapse Meetings Sidebar" : "Expand Meetings Sidebar"}>
              <IconButton
                size="small"
                onClick={onToggleRightSidebar}
                sx={{
                  color: rightSidebarOpen ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
                  bgcolor: rightSidebarOpen ? 'var(--hp-primary-light)' : 'transparent',
                  p: 0.6,
                  borderRadius: '8px'
                }}
              >
                <Event fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Intro / Welcome Card with Collapse Toggle */}
      <Box className="neumorphic-card" sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', width: { xs: 36, sm: 42 }, height: { xs: 36, sm: 42 }, boxShadow: 'inset 2px 2px 4px #FFFFFF' }}>
              <SmartToy fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-text)', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                Hi {userName}! 👋
              </Typography>
              <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ display: 'block' }}>
                Grounded on <strong>{selectedProject.name}</strong>{selectedMeeting ? ` • ${selectedMeeting.title}` : ''}
              </Typography>
            </Box>
          </Box>

          <IconButton size="small" onClick={() => setWelcomeExpanded(!welcomeExpanded)} sx={{ color: 'var(--hp-text-secondary)' }}>
            {welcomeExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
          </IconButton>
        </Box>

        <Collapse in={welcomeExpanded}>
          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.5, mb: 1, lineHeight: 1.45, fontSize: { xs: '0.8rem', sm: '0.86rem' } }}>
            I'm your <strong>ScrumAI Project Manager</strong>. Ask me to synthesize transcripts, author BRDs, track sprint blockers, or generate Jira epics and stories.
          </Typography>

          {/* Suggested Prompts */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
            {[
              { label: 'Summarize latest meeting', icon: <ReceiptLong fontSize="small" /> },
              { label: 'Show project health', icon: <ShowChart fontSize="small" /> },
              { label: 'Generate BRD template', icon: <Assessment fontSize="small" /> },
              { label: 'Create Jira stories', icon: <CheckCircle fontSize="small" /> },
              { label: 'Show action items', icon: <TrendingUp fontSize="small" /> },
              { label: 'Compare sprint progress', icon: <Assessment fontSize="small" /> }
            ].map((prompt, idx) => (
              <Chip
                key={idx}
                icon={prompt.icon}
                label={prompt.label}
                onClick={() => handleSendMessage(prompt.label)}
                size="small"
                sx={{
                  bgcolor: 'var(--hp-surface)',
                  border: '1px solid var(--hp-border)',
                  borderRadius: '8px',
                  color: 'var(--hp-text-secondary)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '0.78rem',
                  py: 0.3,
                  '&:hover': { bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', borderColor: 'var(--hp-primary)' }
                }}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* Chat History Area */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, px: { xs: 0.5, sm: 1 }, pr: { xs: 1, sm: 1.5 } }}>
        {messages.map((msg) => {
          if (msg.sender === 'user') {
            return (
              <Box key={msg.id} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Box
                  sx={{
                    bgcolor: 'var(--hp-primary-light)',
                    color: 'var(--hp-text)',
                    px: { xs: 1.8, sm: 2 },
                    py: 1.2,
                    borderRadius: '16px',
                    borderTopRightRadius: '4px',
                    maxWidth: { xs: '88%', sm: '80%' },
                    boxShadow: '2px 2px 6px var(--hp-shadow-dark)'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.86rem', sm: '0.9rem' } }}>{msg.text}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontSize: '0.68rem', display: 'block', textAlign: 'right', mt: 0.5 }}>
                    {msg.timestamp}
                  </Typography>
                </Box>
                <Avatar src={userAvatar} sx={{ width: 32, height: 32, bgcolor: 'var(--hp-primary)', fontSize: '0.8rem', flexShrink: 0 }}>
                  {userName.charAt(0)}
                </Avatar>
              </Box>
            );
          }

          return (
            <Box key={msg.id} sx={{ display: 'flex', gap: 1.2 }}>
              <Avatar sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', width: 32, height: 32, flexShrink: 0 }}>
                <SmartToy fontSize="small" />
              </Avatar>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: { xs: '92%', sm: '88%' }, minWidth: 0, flex: 1 }}>
                
                {/* Standard Agent HTML Response */}
                {!msg.cardType && (
                  <Box
                    sx={{
                      '& p': { margin: '0 0 8px 0', '&:last-child': { marginBottom: 0 } },
                      '& ul, & ol': { margin: '4px 0 8px 0', paddingLeft: '24px' },
                      '& li': { marginBottom: '4px' },
                      '& a': { color: 'var(--hp-primary)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                      '& strong, & b': { fontWeight: 600 },
                      color: 'var(--hp-text)',
                      fontSize: { xs: '0.86rem', sm: '0.9rem' },
                      lineHeight: 1.5
                    }}
                    dangerouslySetInnerHTML={{ __html: msg.text }}
                  />
                )}

                {/* Card Type: Meeting Summary */}
                {msg.cardType === 'summary' && (
                  <Box sx={{ bgcolor: 'var(--hp-surface-raised)', borderRadius: '14px', p: { xs: 1.5, sm: 2.2 }, border: '1px solid var(--hp-border)', boxShadow: '4px 4px 12px var(--hp-shadow-dark), -4px -4px 12px var(--hp-shadow-light)' }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', width: 34, height: 34, borderRadius: '8px', flexShrink: 0 }}>
                          <ReceiptLong fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--hp-text)' }}>
                            {selectedMeeting ? selectedMeeting.title : 'Product Sync Session'}
                          </Typography>
                          <Typography variant="caption" color="var(--hp-text-secondary)">
                            {selectedMeeting ? `${selectedMeeting.date} • ${selectedMeeting.time}` : 'Oct 16, 2026'} • 8 participants
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDialogInfo({
                          title: `Transcript: ${selectedMeeting ? selectedMeeting.title : 'Session'}`,
                          content: (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <Typography variant="body2"><strong>[10:02 AM] Akhil:</strong> Let's review the mobile authentication requirements.</Typography>
                              <Typography variant="body2"><strong>[10:15 AM] Sarah:</strong> Biometric login using Keychain & Keystore is ready to be ticketed.</Typography>
                              <Typography variant="body2"><strong>[10:28 AM] Dave:</strong> Analytics charts should follow in sprint 24.</Typography>
                            </Box>
                          )
                        })}
                        sx={{ borderRadius: '20px', textTransform: 'none', color: 'var(--hp-primary)', borderColor: 'var(--hp-border)', fontSize: '0.78rem', alignSelf: { xs: 'flex-end', sm: 'auto' } }}
                      >
                        Open meeting →
                      </Button>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, fontSize: '0.88rem' }}>Key Discussion Points</Typography>
                    <ul style={{ margin: '6px 0 14px 0', paddingLeft: 18, color: 'var(--hp-text-secondary)', fontSize: '0.84rem' }}>
                      <li>Reviewed progress on mobile app v2.0 features</li>
                      <li>Discussed biometric authentication flow and security compliance</li>
                      <li>Agreed on timeline for beta release (Nov 15, 2026)</li>
                      <li>Identified blockers in the analytics dashboard</li>
                    </ul>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Autonomous Jira & BRD Actions</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      {[
                        { text: 'Created 1 Epic in Jira: Mobile App v2.0', link: 'MOB-1234', icon: <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 18 }} /> },
                        { text: 'Created 4 Stories for authentication flow', link: 'MOB-1235..1238', icon: <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 18 }} /> },
                        { text: 'Created 2 Stories for analytics dashboard', link: 'MOB-1241..1242', icon: <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 18 }} /> },
                        { text: 'Generated BRD template for Mobile App v2.0', link: 'View BRD →', icon: <Assessment sx={{ color: 'var(--hp-primary)', fontSize: 18 }} /> }
                      ].map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            {item.icon}
                            <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.text}</Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="var(--hp-primary)"
                            sx={{ fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0, '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => setDialogInfo({
                              title: `Item Details: ${item.link}`,
                              content: <Typography variant="body2">Detailed Jira payload and acceptance criteria for {item.text}.</Typography>
                            })}
                          >
                            {item.link}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSendMessage('Show me the created Jira stories')}
                        sx={{ borderRadius: '8px', textTransform: 'none', color: 'var(--hp-text)', borderColor: 'var(--hp-border)', fontSize: '0.78rem' }}
                      >
                        Show Jira items
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSendMessage('What are the main blockers?')}
                        sx={{ borderRadius: '8px', textTransform: 'none', color: 'var(--hp-text)', borderColor: 'var(--hp-border)', fontSize: '0.78rem' }}
                      >
                        Inspect Blockers
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSendMessage('Generate BRD template')}
                        sx={{ borderRadius: '8px', textTransform: 'none', color: 'var(--hp-text)', borderColor: 'var(--hp-border)', fontSize: '0.78rem' }}
                      >
                        Generate BRD
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Card Type: Scrum Health */}
                {msg.cardType === 'health' && (
                  <Box sx={{ bgcolor: 'var(--hp-surface-raised)', borderRadius: '14px', p: 2, border: '1px solid var(--hp-border)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{selectedProject.name} — Scrum Health: {selectedProject.healthScore}/100</Typography>
                    <Box sx={{ my: 1.5 }}>
                      <LinearProgress variant="determinate" value={selectedProject.healthScore} sx={{ height: 8, borderRadius: 4, bgcolor: 'var(--hp-border)', '& .MuiLinearProgress-bar': { bgcolor: 'var(--hp-success)' } }} />
                    </Box>
                    <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ fontSize: '0.85rem' }}>
                      • On Track: <strong>{selectedProject.onTrackPct}%</strong><br />
                      • At Risk: <strong>{selectedProject.atRiskPct}%</strong><br />
                      • Blocked: <strong>{selectedProject.blockedPct}%</strong>
                    </Typography>
                  </Box>
                )}

                {/* Card Type: Jira Stories */}
                {msg.cardType === 'jira' && (
                  <Box sx={{ bgcolor: 'var(--hp-surface-raised)', borderRadius: '14px', p: 2, border: '1px solid var(--hp-border)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Generated Jira Issues in {selectedProject.name}:</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ p: 1, bgcolor: 'var(--hp-bg)', borderRadius: '8px', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.84rem' }}><strong>MOB-1235:</strong> Implement Keychain biometric authentication</Typography>
                        <Chip size="small" label="Story" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                      </Box>
                      <Box sx={{ p: 1, bgcolor: 'var(--hp-bg)', borderRadius: '8px', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.84rem' }}><strong>MOB-1236:</strong> Configure Keystore fallback PIN code</Typography>
                        <Chip size="small" label="Story" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                      </Box>
                      <Box sx={{ p: 1, bgcolor: 'var(--hp-bg)', borderRadius: '8px', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.84rem' }}><strong>MOB-1241:</strong> Analytics latency telemetry dashboard</Typography>
                        <Chip size="small" label="Task" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Card Type: BRD */}
                {msg.cardType === 'brd' && (
                  <Box sx={{ bgcolor: 'var(--hp-surface-raised)', borderRadius: '14px', p: 2, border: '1px solid var(--hp-border)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--hp-primary)' }}>Business Requirements Document (Draft)</Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: 'var(--hp-text)', fontSize: '0.85rem' }}>
                      <strong>1. Executive Summary:</strong> Modernization of biometric auth for {selectedProject.name}.<br />
                      <strong>2. In-Scope:</strong> iOS FaceID/TouchID, Android Biometrics, Passcode fallback.<br />
                      <strong>3. Delivery Target:</strong> Beta deployment Nov 15, 2026.
                    </Typography>
                  </Box>
                )}

                {/* Card Type: Blockers */}
                {msg.cardType === 'blockers' && (
                  <Box sx={{ bgcolor: 'var(--hp-surface-raised)', borderRadius: '14px', p: 2, border: '1px solid var(--hp-border)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--hp-danger)' }}>Sprint Blockers Detected (10% Risk):</Typography>
                    <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                      • Analytics DB indexing latency pending DBA sign-off.<br />
                      • Security compliance questionnaire needs approval by SecOps team.
                    </Typography>
                  </Box>
                )}

                {/* Follow up suggestions */}
                <Box sx={{ display: 'flex', gap: 0.8, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip label="What are the main blockers?" variant="outlined" onClick={() => handleSendMessage('What are the main blockers?')} sx={{ borderColor: 'var(--hp-border)', color: 'var(--hp-text-secondary)', fontSize: '0.75rem', '&:hover': { bgcolor: 'var(--hp-surface-raised)' } }} />
                  <Chip label="Show me the created Jira stories" variant="outlined" onClick={() => handleSendMessage('Show me the created Jira stories')} sx={{ borderColor: 'var(--hp-border)', color: 'var(--hp-text-secondary)', fontSize: '0.75rem', '&:hover': { bgcolor: 'var(--hp-surface-raised)' } }} />
                  <Chip label="Generate release plan" variant="outlined" onClick={() => handleSendMessage('Generate release plan')} sx={{ borderColor: 'var(--hp-border)', color: 'var(--hp-text-secondary)', fontSize: '0.75rem', '&:hover': { bgcolor: 'var(--hp-surface-raised)' } }} />
                </Box>

              </Box>
            </Box>
          );
        })}

        {isTyping && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', width: 32, height: 32 }}>
              <SmartToy fontSize="small" />
            </Avatar>
            <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
              ScrumAI is thinking and grounding answer...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Message Composer Anchored at Bottom */}
      {agent.user !== undefined && !agent.isConnected ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1, borderTop: '1px solid var(--hp-border)', flexShrink: 0 }}>
          <Button variant="contained" color="primary" onClick={agent.signIn} sx={{ borderRadius: 20, textTransform: 'none', px: 4, py: 1 }}>
            Login to Atlassian
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pt: 1, borderTop: '1px solid var(--hp-border)', flexShrink: 0 }}>
          <Tooltip title="Add document or BRD template">
            <IconButton
              sx={{
                bgcolor: 'var(--hp-surface-raised)',
                boxShadow: '2px 2px 5px var(--hp-shadow-dark), -2px -2px 5px var(--hp-shadow-light)',
                width: { xs: 38, sm: 42 },
                height: { xs: 38, sm: 42 },
                borderRadius: '12px'
              }}
              onClick={() => handleSendMessage('Generate BRD template')}
            >
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box className="neumorphic-input" sx={{ flex: 1, display: 'flex', alignItems: 'center', px: { xs: 1.5, sm: 2 }, py: 0.8, borderRadius: '24px' }}>
            <InputBase
              placeholder="Ask ScrumAI anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={agent.busy}
              sx={{ flex: 1, fontSize: { xs: '0.86rem', sm: '0.92rem' }, color: 'var(--hp-text)' }}
            />
          </Box>

          <IconButton
            onClick={() => handleSendMessage()}
            disabled={!input.trim() || agent.busy}
            sx={{
              bgcolor: 'var(--hp-primary)',
              color: 'white',
              width: { xs: 38, sm: 42 },
              height: { xs: 38, sm: 42 },
              borderRadius: '12px',
              boxShadow: '4px 4px 10px rgba(79, 107, 255, 0.3)',
              '&:hover': { bgcolor: 'var(--hp-primary-dark)' },
              '&.Mui-disabled': { bgcolor: 'var(--hp-border)', color: 'var(--hp-text-secondary)' }
            }}
          >
            <Send fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={Boolean(dialogInfo)} onClose={() => setDialogInfo(null)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{dialogInfo?.title}</Typography>
          <IconButton size="small" onClick={() => setDialogInfo(null)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialogInfo?.content}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogInfo(null)} sx={{ color: 'var(--hp-text-secondary)', textTransform: 'none' }}>Close</Button>
          <Button variant="contained" onClick={() => setDialogInfo(null)} sx={{ bgcolor: 'var(--hp-primary)', borderRadius: '10px', textTransform: 'none' }}>Done</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ChatPanel;
